import { useState } from 'react';
import { boardLink } from '../utils/board-navigation';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import SaveConflict from '../../../common/components/save-conflict/save-conflict';
import { useTask, useUpdateTask } from '../contexts/tasks';
import {
    WORKFLOW_STATE_LABEL, hasChanges, toUpdatePayload, validate, valuesFrom,
} from '../utils/form';
import TaskFields from './fields';

import '../../clients/styles/form.css';

export default function EditTaskForm({ taskId }) {
    const { data, isLoading, error, refetch } = useTask(taskId);

    if (isLoading) return <p className="client-form__loading">Cargando tarea...</p>;

    if (error) {
        return (
            <p className="client-form__failure">
                {error.message || 'No se pudo cargar la tarea'}
            </p>
        );
    }

    const task = data?.task ?? data;
    return <TaskEditor key={task.id} task={task} refetch={refetch} />;
}

// The seven fields that make up the specification, which are the ones a
// revision conflict can be about. `priority` and `status` never are.
const SPEC_LABELS = {
    title: 'Título',
    kind: 'Tipo',
    estimate: 'Talla',
    description: 'Descripción',
    acceptance_criteria: 'Criterios de aceptación',
    scope: 'Ámbito',
    open_questions: 'Dudas abiertas',
};

function TaskEditor({ task, refetch }) {
    const [params] = useSearchParams();
    const updateTask = useUpdateTask(task.id);

    // `base` is the row the draft was typed on top of; `task` is what the
    // server holds right now. Diffing the payload against `base` is what keeps
    // an untouched field out of the request — which matters here more than
    // anywhere, because an authorized task is frozen and resending its title
    // verbatim would still be asking to write a frozen column.
    const [base, setBase] = useState(task);
    const [values, setValues] = useState(() => valuesFrom(task));
    const [errors, setErrors] = useState({});
    const [conflict, setConflict] = useState(false);

    // An authorization freezes the specification and the dependencies by
    // trigger, through every door. Offering a save here is offering a button
    // that cannot work.
    const authorized = !!(task.authorization || task.authorized_at);

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const takeTheirs = async () => {
        const { data } = await refetch();
        const fresh = data?.task ?? data;
        if (fresh) {
            setBase(fresh);
            setValues(valuesFrom(fresh));
        }
        setConflict(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate(values, { withPlacement: false });
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        const payload = toUpdatePayload(values, base, base.spec_revision);

        if (!hasChanges(payload)) {
            toast('No hay cambios que guardar');
            return;
        }

        try {
            const saved = await updateTask.mutateAsync(payload);
            const row = saved?.task ?? saved;
            if (row) {
                setBase(row);
                setValues(valuesFrom(row));
            }
            setConflict(false);
            toast.success('Tarea guardada');
        } catch (err) {
            if (err.isRevisionConflict) {
                setConflict(true);
                await refetch();
                return;
            }
            if (err.isFrozen) {
                await refetch();
                toast.error('La especificación está autorizada: quedó firmada y no se edita.');
                return;
            }
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo guardar la tarea');
        }
    };

    const state = WORKFLOW_STATE_LABEL[task.workflow_state] ?? task.workflow_state;
    const theirs = valuesFrom(task);
    const diff = Object.entries(SPEC_LABELS).map(([name, label]) => ({
        name, label, mine: values[name], theirs: theirs[name],
    }));

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">{task.title}</h2>
                <p className="client-form__subtitle">
                    Tarea #{task.id}
                    {state && (
                        <> · <span className={`task-state task-state--${task.workflow_state}`}>{state}</span></>
                    )}
                    {base.spec_revision != null && <> · Especificación rev. {base.spec_revision}</>}
                </p>
            </header>

            {conflict && (
                <SaveConflict
                    fields={diff}
                    onReload={takeTheirs}
                    onDismiss={() => setConflict(false)}
                />
            )}

            {authorized && (
                <p className="client-form__failure">
                    Esta tarea fue autorizada: su especificación y sus dependencias quedaron
                    firmadas y no cambian por ninguna puerta. Autorizar dice qué trabajo se hace,
                    no que esté hecho — el estado, el peso y la reserva siguen su curso.
                </p>
            )}

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos de la tarea</h3>
                        <p className="client-form__section-text">
                            El estado no se edita: una tarea se mueve al tomarla, entregarla
                            o revisarla, y esa es la reserva atómica del sistema. Cambiar la
                            especificación sube su revisión.
                        </p>
                    </div>

                    <TaskFields
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={updateTask.isPending}
                        placementLocked
                        weightLocked
                        projectId={task.project_id}
                        tierLabel={task.tier?.name}
                        repositoryLabel={task.repository?.name}
                    />
                </div>

                <div className="client-form__actions">
                    <Link to={boardLink('/admin/development/tareas', params)} className="client-form__cancel">Volver</Link>
                    <button type="submit" className="client-form__submit" disabled={updateTask.isPending}>
                        {updateTask.isPending ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </form>

            <p className="client-form__aside">
                Las dependencias —de qué tareas espera esta— tienen su propio endpoint
                y todavía no tienen pantalla.
            </p>
        </section>
    );
}
